import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { groups } from '../lib/api'
import { useActionBoundary } from '../hooks/useActionBoundary'
import { useAuth } from '../contexts/AuthContext'
import { usePerson } from '../contexts/PersonContext'
import { useToast } from '../contexts/ToastContext'
import { useInvalidateGroups } from '../hooks/useGroups'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { EmptyState } from '../components/ui/EmptyState'
import type { GroupInvitationDto } from '../lib/api/groups'

/** Invitations are offers to a person, not automatic circle enrollment. */
export function GroupInvitationsPage() {
  const { role, flags, activePerson, guardianFirst } = usePerson()
  const { token, getSessionGeneration } = useAuth()
  const generation = getSessionGeneration()
  const capture = useActionBoundary('group-invitations')
  const { flash } = useToast()
  const invalidate = useInvalidateGroups()
  const query = useQuery({ queryKey: ['community', 'group-invitations', activePerson?.id], queryFn: groups.listInvitations, retry: false })
  const [rows, setRows] = useState<Record<string, { busy?: boolean; error?: string; done?: boolean }>>({})
  const pending = useRef(new Set<string>())
  useEffect(() => { setRows({}); pending.current = new Set() }, [activePerson?.id, token, generation])
  async function decide(invitation: GroupInvitationDto, accept: boolean) {
    if (pending.current.has(invitation.id) || (accept && flags.viewOnly)) return
    const current = capture()
    const startedPending = pending.current
    startedPending.add(invitation.id); setRows((old) => ({ ...old, [invitation.id]: { busy: true } }))
    try {
      if (accept) await groups.acceptInvitation(invitation.groupId, invitation.id)
      else await groups.declineInvitation(invitation.groupId, invitation.id)
      if (!current()) return
      setRows((old) => ({ ...old, [invitation.id]: { done: true } }))
      invalidate(invitation.groupId)
      flash(!accept ? 'Invitation declined.' : role === 'kid' && invitation.needsGuardian ? `Sent to ${guardianFirst || 'your guardian'} for approval.` : 'Invitation accepted. Player membership is confirmed.')
    } catch (error) {
      if (current()) setRows((old) => ({ ...old, [invitation.id]: { error: error instanceof Error ? error.message : 'That decision did not go through. Try again.' } }))
    } finally { startedPending.delete(invitation.id) }
  }
  const invitations = (query.data ?? []).filter((invitation) => !rows[invitation.id]?.done)
  return <div className="animate-rise px-5 py-6 sm:px-10 sm:py-9">
    <h1 className="font-serif text-[30px] text-ink">Group invitations</h1>
    <p className="mt-3 text-sm leading-relaxed text-ink-muted">Each player chooses whether to join. A guardian decides for a managed profile; accepting a circle invitation does not enroll everyone in a group.</p>
    {query.isLoading ? <p role="status" className="mt-5">Loading invitations…</p> : query.isError ? <div role="alert" className="mt-5"><p>Invitations could not be loaded.</p><Button className="mt-3" variant="outline" onClick={() => void query.refetch()}>Retry invitations</Button></div> : invitations.length === 0 ? <EmptyState className="mt-5" title="No pending invitations" description="New group invitations will appear here." action={<Link to="/browse">Browse groups</Link>} /> : <div className="mt-5 grid gap-4">
      {invitations.map((invitation) => <Card key={invitation.id}>
        <h2 className="font-serif text-xl text-ink">{invitation.groupName}</h2>
        <p className="mt-1 text-sm text-ink-muted">{invitation.game}{invitation.viaCircleName ? ` · via ${invitation.viaCircleName}` : ''} · for {invitation.forFirstName || 'your profile'}</p>
        {rows[invitation.id]?.error && <p role="alert" className="mt-3 text-sm text-honey-fg">{rows[invitation.id].error}</p>}
        {flags.viewOnly && <p className="mt-3 text-sm text-ink-muted">Ask {guardianFirst || 'your guardian'} to accept from their profile.</p>}
        <div className="mt-4 flex flex-wrap gap-3">
          {!flags.viewOnly && <Button variant="accent" disabled={rows[invitation.id]?.busy} onClick={() => void decide(invitation, true)}>{rows[invitation.id]?.busy ? 'Saving…' : role === 'parent' && invitation.needsGuardian ? `Accept for ${invitation.forFirstName}` : invitation.needsGuardian ? 'Ask guardian to approve' : 'Accept invitation'}</Button>}
          <Button variant="outline" disabled={rows[invitation.id]?.busy} onClick={() => void decide(invitation, false)}>Decline invitation</Button>
        </div>
      </Card>)}
    </div>}
  </div>
}
